import React, { FunctionComponent, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';

import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  FillPersonalIcon,
  GeneratePersonalIcon,
  UpdatePersonalIcon,
} from 'assets/personal_statement_builder';

import styles from './PersonalStatementBuilder.module.scss';

import Text from 'common/components/Text/Text';
import { SessionState } from 'features/session/sessionSlice';
import { useAddPersonalStatementMutation } from 'services/exploreApi';
import TagManager from 'react-gtm-module';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';

interface GeneralStepProps {
  title: string;
  description: string;
  icon?: string;
}

const GeneralStep = ({ title, description, icon }: GeneralStepProps): React.ReactElement => {
  return (
    <div className={styles.step}>
      <img src={icon}></img>
      <Text
        size={30}
        weight={700}
        inlineStyle={{ color: '#193676', marginTop: '40px', marginBottom: '12px' }}
      >
        {title}
      </Text>
      <Text
        size={18}
        weight={600}
        inlineStyle={{ color: '#727D9B', maxWidth: '60%', textAlign: 'center' }}
      >
        {description}
      </Text>
    </div>
  );
};

function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
  return typeof error === 'object' && error != null && 'status' in error;
}

const PersonalStatementBuilderIndex: FunctionComponent = () => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const navigate = useNavigate();
  const { logged } = useAppSelector(({ session }: { session: SessionState }) => session);
  const [warningAdvice, setWarningAdvice] = useState<string>('');
  const [submitPersonalStatement, { error: newPersonalStatementError }] =
    useAddPersonalStatementMutation();

  const handleClick = () => {
    if (logged) {
      submitPersonalStatement().then((resp: any) => {
        try {
          sendGTMEvent('ps_builder_start');
          navigate(`/personal-statements/${resp.data.data.id}`);
        } catch (error) {
          console.log(error);
        }
      });
    } else {
      navigate('/login/flow=personal-statement-builder');
    }
  };
  const sendGTMEvent = (eventName: string) => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'ga_event',
        eventName: eventName,
      },
    });
  };

  useEffect(() => {
    if (isFetchBaseQueryError(newPersonalStatementError)) {
      console.log(newPersonalStatementError.data);
      const err = JSON.stringify(newPersonalStatementError.data);
      if (
        JSON.parse(err)['meta']['errors']['user_id'] ===
        "User can't have more than 3 pending personal statements"
      ) {
        setWarningAdvice('You already have 3 pending personal statements');
      } else if (
        JSON.parse(err)['meta']['errors']['user_id'] ===
        "User can't have more than 30 personal statements"
      ) {
        setWarningAdvice('You already have 30 personal statements');
      }
    }
    console.log(newPersonalStatementError);
  }, [newPersonalStatementError]);

  return (
    <div className={styles.statementBuilderIndex}>
      <Text size={30} weight={600} inlineStyle={{ color: '#19234F', marginBottom: '16px' }}>
        Welcome to Unify AI Personal Statement Builder
      </Text>
      <Text size={24} weight={600} inlineStyle={{ color: '#297DF2' }}>
        Simple steps to craft your impressive personal statement
      </Text>
      <div className={styles.stepsContainer}>
        <GeneralStep
          title="Fill"
          description="Answer curated questions about yourself and your motivations"
          icon={FillPersonalIcon}
        />
        <GeneralStep
          title="Generate"
          description="AI will generate a customized personal statement"
          icon={GeneratePersonalIcon}
        />
        <GeneralStep
          title="Update"
          description="Edit your responses & re-generate your personal statement at any time"
          icon={UpdatePersonalIcon}
        />
      </div>
      <button className={`btn btn-primary nav-link ${styles.startButton}`} onClick={handleClick}>
        Start
      </button>
      {newPersonalStatementError && (
        <div className={styles.warningLabel}>
          {warningAdvice}.&nbsp;Go to your{' '}
          <a href="/personal-statements" style={{ color: '#297DF2' }}>
            Hub
          </a>
        </div>
      )}
    </div>
  );
};

export default PersonalStatementBuilderIndex;
